export const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    }
    : null;
};

export const rgbToHsl = (r: number, g: number, b: number) => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return [h, s, l];
};

export const hslToHex = (h: number, s: number, l: number) => {
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const getColorDifference = (color1: string, color2: string) => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) return 100;

  const rDiff = Math.abs(rgb1.r - rgb2.r);
  const gDiff = Math.abs(rgb1.g - rgb2.g);
  const bDiff = Math.abs(rgb1.b - rgb2.b);

  return Math.round((rDiff + gDiff + bDiff) / 7.65);
};

export const isColorSimilar = (
  color1: string,
  color2: string,
  tolerance: number
) => {
  return getColorDifference(color1, color2) <= tolerance;
};

export const SKIN_TONES = [
  "#FFC8A0", // Light
  "#FFB080", // Medium
  "#E09060", // Dark
  "#FFD1B0", // Pink tone
  "#FFE0C0", // Ivory
];

export const HAIR_COLORS = [
  "#000000", // Black
  "#4A3421", // Dark brown
  "#8B4513", // Brown
  "#D4A017", // Golden
  "#8B4C39", // Auburn
  "#708090", // Gray
];

/**
 * 将HEX颜色字符串转换为32位整数 (RGBA格式)
 * 支持以下格式:
 * - #RGB (如 #F00)
 * - #RGBA (如 #F00F)
 * - #RRGGBB (如 #FF0000)
 * - #RRGGBBAA (如 #FF0000FF)
 * - 不带#的以上格式
 *
 * @param {string} hexString HEX颜色字符串
 * @return {number} 32位整数表示的RGBA颜色
 */
export const hexToInt = (hexString: string): number => {
  // 移除可能的#前缀
  hexString = hexString.replace(/^#/, "");

  // 扩展简写形式的HEX (#RGB 或 #RGBA)
  if (hexString.length === 3 || hexString.length === 4) {
    hexString = Array.from(hexString)
      .map((c) => c + c)
      .join("");
  }

  // 处理RRGGBB形式 (无Alpha通道)
  if (hexString.length === 6) {
    hexString += "FF"; // 默认完全不透明
  }

  // 解析各个颜色通道
  const r = parseInt(hexString.substring(0, 2), 16);
  const g = parseInt(hexString.substring(2, 4), 16);
  const b = parseInt(hexString.substring(4, 6), 16);
  const a = parseInt(hexString.substring(6, 8), 16);

  // 组合为32位整数 (RGBA格式)
  return (a << 24) | (r << 16) | (g << 8) | b;
};

/**
 * 将32位整数颜色值转换为HEX字符串
 *
 * @param {number} colorInt 32位整数表示的RGBA颜色值
 * @param {boolean} includeAlpha 是否包含Alpha通道在输出中
 * @param {boolean} includeHash 是否在输出前加#前缀
 * @return {string} HEX格式的颜色字符串
 */
export const intToHex = (
  colorInt: number,
  includeAlpha: boolean = true,
  includeHash: boolean = true
): string => {
  // 提取各个颜色通道
  const r = (colorInt >> 16) & 0xff;
  const g = (colorInt >> 8) & 0xff;
  const b = colorInt & 0xff;
  const a = (colorInt >> 24) & 0xff;

  // 转换为HEX字符串并确保每个通道都是两位数
  const hexR = r.toString(16).padStart(2, "0");
  const hexG = g.toString(16).padStart(2, "0");
  const hexB = b.toString(16).padStart(2, "0");
  const hexA = a.toString(16).padStart(2, "0");

  // 组合结果
  let result = hexR + hexG + hexB;
  if (includeAlpha) {
    result += hexA;
  }

  // 添加#前缀
  if (includeHash) {
    result = "#" + result;
  }

  return result.toUpperCase(); // 返回大写形式
};

/**
 * 将32位整数颜色值转换为RGB对象
 *
 * @param {number} colorInt 32位整数表示的RGBA颜色值
 * @return {{ r: number; g: number; b: number }} 包含RGB通道值的对象
 */
export const intToRgb = (
  colorInt: number
): { r: number; g: number; b: number } => {
  // 提取各个颜色通道
  const r = (colorInt >> 16) & 0xff;
  const g = (colorInt >> 8) & 0xff;
  const b = colorInt & 0xff;

  return { r, g, b };
};

export const rgbToInt = (r: number, g: number, b: number) => {
  return (r << 16) | (g << 8) | b;
};
